import moment from "moment";

export const COMPLIANCE_ISSUE_FIELDS = [
  {
    key: "is_valid_counselling",
    label: "Pharmacist/Pharmacy Risk Confirmation Missing",
  },
  { key: "is_valid_cycles", label: "Number of Cycles Invalid" },
  {
    key: "is_valid_cycles_wcbp",
    label: "Number of Cycles (WCBP) Invalid",
  },
  { key: "is_valid_n_weeks", label: "Number of Weeks Invalid" },
  {
    key: "is_valid_n_weeks_wcbp",
    label: "Number of Weeks (WCBP) Invalid",
  },
  { key: "is_valid_n_capsules", label: "Number of Capsules Invalid" },
  { key: "is_valid_pregnancy_test", label: "Pregnancy Test Invalid" },
  { key: "is_valid_range_date", label: "Date Range Invalid" },
  { key: "is_valid_indication", label: "Off-Licence Other Indication Invalid" },
  { key: "is_valid_capsule_strength", label: "Capsule Strength Invalid" },
];

/**
 * Resolves whether to show a compliance indicator for a prescription.
 * Only explicit `true` is Compliant; `false` is Non-Compliant only for Rejected PAFs.
 * Otherwise returns undefined (render empty — do not default to Non-Compliant).
 */
export function resolveComplianceIndicator(source) {
  if (!source) return undefined;
  const indicator = source.compliance_indicator;
  if (indicator !== true && indicator !== false) return undefined;
  if (indicator === true) return true;
  const status = source.status_name ?? source.status;
  if (indicator === false && status === "Rejected") return false;
  return undefined;
}

/**
 * Patient key for GET /users/:id/prescription-complaint/
 * e.g. users/WCBPAAA001212001/prescription-complaint/
 */
export function resolvePrescriptionComplaintUserKey(
  patientDetails,
  form,
  receivedForm
) {
  const candidates = [
    patientDetails?.id,
    patientDetails?.patient_id,
    patientDetails?.registration_number,
    form?.patient?.id,
    form?.patient?.patient_id,
    form?.patient_id,
    form?.patient?.registration_number,
    receivedForm?.patient?.id,
    receivedForm?.patient?.patient_id,
    receivedForm?.patient_id,
  ];

  for (const value of candidates) {
    if (value != null && value !== "") return String(value);
  }
  return null;
}

export function getComplaintIssues(complaint) {
  if (!complaint) return [];
  return COMPLIANCE_ISSUE_FIELDS.filter(({ key }) => complaint[key] === false).map(
    ({ label }) => label
  );
}

export function formatComplaintIssuesText(complaint) {
  const issues = getComplaintIssues(complaint);
  return issues.length ? issues.join(" ") : "—";
}

export function formatComplaintNCycles(nCycles) {
  if (nCycles == null || nCycles === "") return "—";
  const match = String(nCycles).match(/^(\d+)/);
  return match ? match[1] : String(nCycles);
}

/** Non-compliant when any `is_valid_*` flag is explicitly false. */
export function getComplaintComplianceIndicator(complaint) {
  if (!complaint) return undefined;
  if (complaintHasIssues(complaint)) return false;
  const hasPassingCheck = COMPLIANCE_ISSUE_FIELDS.some(
    ({ key }) => complaint[key] === true
  );
  return hasPassingCheck ? true : undefined;
}

export function complaintHasIssues(complaint) {
  return getComplaintIssues(complaint).length > 0;
}

export function formatComplaintDate(iso) {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("DD-MMM-YYYY").toUpperCase() : "—";
}

export function sortComplaintsByDateDesc(complaints) {
  return [...(complaints ?? [])].sort((a, b) => {
    const ta = new Date(a.prescription_created_date || a.created_at || 0).getTime();
    const tb = new Date(b.prescription_created_date || b.created_at || 0).getTime();
    return tb - ta;
  });
}

export function getComplaintsWithIssues(complaints) {
  return sortComplaintsByDateDesc(complaints).filter(complaintHasIssues);
}
